import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Loader2 } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../../../../../components/ui/dialog';
import {
  clearNDTIndicationLogs,
  fetchNDTIndicationLogs,
} from '../../../../../redux/ProductionData/actions';
import type { PipeOption } from '../../../../ndt/components/FilterPanel';
import { NdtRepairLogModal } from '../../../../project/components/dashboard/production/NDTIndicationLog/NdtRepairLogModal';
import {
  applyNdtCacheToRows,
  computeVariantKpis,
} from '../../../utils/drillDownKpis';
import {
  fetchNdtIndicationLogsApi,
} from '../../../utils/fetchNdtIndicationLogsApi';
import {
  fetchNdtLogsForPipes,
  indexNdtLogsForRows,
  logsHaveWeldIdentity,
} from '../../../utils/ndtLogIndex';
import {
  fetchPassViewRecords,
  fetchProjectLimitsApi,
  fetchTiltViewRecords,
  fetchZoneViewRecords,
  collectViolationsForWeld,
  type PassViewRecord,
} from '../../../utils/fetchWeldViewApis';
import { runWithConcurrency } from '../../../utils/runWithConcurrency';
import {
  useWpsViolationsBatchLoader,
  WPS_BATCH_FETCH_CAP,
} from '../../../utils/useWpsViolationsBatch';
import type { WeldDrillDownPrefetchState } from '../../../utils/useWeldDrillDownPrefetch';
import { formatNdtPrefetchProgress } from '../../../utils/formatNdtPrefetchProgress';
import { buildDrillDownSessionKey } from '../../../utils/buildDrillDownSessionKey';
import type { PipeJobRef } from '../../../utils/jobNumbersFromSetup';
import {
  type LimitsRow,
  type WpsViolationRecord,
  limitsConfiguredForJob,
  violationsLackLimits,
} from '../../../utils/wpsViolationModel';
import {
  clearWeldDetailCacheForSession,
  getCachedWeldDetail,
  isWeldDetailCacheFresh,
  setCachedWeldDetail,
  weldDetailCacheKey,
} from '../../../utils/weldDetailCache';
import {
  buildWeldDrillDownRows,
  filterByOutcome,
  rowKey,
} from '../../../utils/weldDrillDownModel';
import type { DashboardMetrics } from '../../../utils/weldDashboardMetrics';
import type { OverviewWeldRow } from '../../WeldOverviewTable';
import type { WeldAnalyticsBundle } from '../../../types/weld';
import { DrillDownKpiStrip } from './DrillDownKpiStrip';
import type { NdtIndicationRow, WeldDrillDownRow, WpsDrillDownVariant } from './drillDownTypes';
import { mapDrillDownRowToWeldData } from './mapDrillDownRowToWeldData';
import { WeldDrillDownRightPanel } from './WeldDrillDownRightPanel';
import { WeldDrillDownTable } from './WeldDrillDownTable';

const NDT_BATCH_CONCURRENCY = 8;

const VARIANT_META: Record<
  WpsDrillDownVariant,
  { title: string; filter: 'pass' | 'fail' | 'all'; showOutcome: boolean }
> = {
  within: {
    title: 'Welds within WPS limit',
    filter: 'pass',
    showOutcome: false,
  },
  out: {
    title: 'Welds out of WPS limit',
    filter: 'fail',
    showOutcome: false,
  },
  compliance: {
    title: 'WPS limit compliance rate',
    filter: 'all',
    showOutcome: true,
  },
};

interface WpsOutcomeDrillDownHostProps {
  variant: WpsDrillDownVariant | null;
  onClose: () => void;
  metrics: DashboardMetrics;
  productionWeldData: OverviewWeldRow[];
  bundle: WeldAnalyticsBundle | null;
  statusCalculationMethod: string;
  projectId: number | null;
  pipeId: string | null;
  pipeOptions?: PipeOption[];
  canEditNdt?: boolean;
  preselectWeld?: { weldNumber: string; jobNumber: string } | null;
  /**
   * Stable single-weld identity for table filtering. Unlike preselectWeld, this is
   * not cleared after the row is selected — keeps scoped drill-downs from expanding.
   */
  weldIdentityFilter?: { weldNumber: string; jobNumber: string } | null;
  singleWeldOnly?: boolean;
  /** When set, left table shows all welds for this job number only. */
  jobOnlyFilter?: string | null;
  onPreselectResult?: (result: 'selected' | 'missing') => void;
  /** Shared Apply-time prefetch; when set, host hydrates and only fills gaps. */
  prefetch?: WeldDrillDownPrefetchState | null;
  appliedPipeIds?: string[];
  appliedJobRefs?: PipeJobRef[];
}

export const WpsOutcomeDrillDownHost = ({
  variant,
  onClose,
  metrics,
  productionWeldData,
  bundle,
  statusCalculationMethod,
  projectId,
  pipeId,
  pipeOptions = [],
  canEditNdt = false,
  preselectWeld = null,
  weldIdentityFilter = null,
  singleWeldOnly = false,
  jobOnlyFilter = null,
  onPreselectResult,
  prefetch = null,
  appliedPipeIds = [],
  appliedJobRefs = [],
}: WpsOutcomeDrillDownHostProps) => {
  const dispatch = useDispatch();
  const [selectedRow, setSelectedRow] = useState<WeldDrillDownRow | null>(null);
  const [editNdtOpen, setEditNdtOpen] = useState(false);
  const [localNdtCache, setLocalNdtCache] = useState<Record<string, NdtIndicationRow[]>>({});
  const [localNdtBatchLoading, setLocalNdtBatchLoading] = useState(false);
  const [localNdtBatchError, setLocalNdtBatchError] = useState<string | null>(null);
  const [localWelderCache, setLocalWelderCache] = useState<Record<string, string>>({});
  const [localPassesByRowKey, setLocalPassesByRowKey] = useState<Record<string, PassViewRecord[]>>({});
  const [wpsPassLoadingKey, setWpsPassLoadingKey] = useState<string | null>(null);
  const [wpsViolationsLoadingKey, setWpsViolationsLoadingKey] = useState<string | null>(null);
  const [localLimits, setLocalLimits] = useState<LimitsRow[]>([]);
  const [localViolationsByRowKey, setLocalViolationsByRowKey] = useState<
    Record<string, WpsViolationRecord[]>
  >({});
  const [wpsDataError, setWpsDataError] = useState<{ key: string; message: string } | null>(null);
  const [localWpsBatchLoading, setLocalWpsBatchLoading] = useState(false);
  const [limitsEpoch, setLimitsEpoch] = useState(0);
  const batchRunRef = useRef(0);
  const limitsRef = useRef<LimitsRow[]>([]);
  const ndtCacheRef = useRef(localNdtCache);
  const violationsByRowKeyRef = useRef(localViolationsByRowKey);
  const welderCacheRef = useRef(localWelderCache);
  const passesByRowKeyRef = useRef(localPassesByRowKey);
  const ndtInFlightRef = useRef(new Set<string>());
  const wpsInFlightRef = useRef(new Set<string>());
  const ndtScopeLogsRef = useRef<{ pipes: Set<number>; logs: NdtIndicationRow[] }>({
    pipes: new Set(),
    logs: [],
  });
  const dataSessionKeyRef = useRef<string | null>(null);
  const limitsLoadedForSessionRef = useRef(false);
  const limitsPromiseRef = useRef<Promise<LimitsRow[]> | null>(null);
  const { loadBatch, cancelBatch } = useWpsViolationsBatchLoader();

  const usingPrefetch = Boolean(prefetch);

  const ndtCache = usingPrefetch ? prefetch!.ndtCache : localNdtCache;
  const setNdtCache = usingPrefetch ? prefetch!.setNdtCache : setLocalNdtCache;
  const welderCache = usingPrefetch ? prefetch!.welderCache : localWelderCache;
  const setWelderCache = usingPrefetch ? prefetch!.setWelderCache : setLocalWelderCache;
  const passesByRowKey = usingPrefetch ? prefetch!.passesByRowKey : localPassesByRowKey;
  const setPassesByRowKey = usingPrefetch
    ? prefetch!.setPassesByRowKey
    : setLocalPassesByRowKey;
  const violationsByRowKey = usingPrefetch
    ? prefetch!.violationsByRowKey
    : localViolationsByRowKey;
  const setViolationsByRowKey = usingPrefetch
    ? prefetch!.setViolationsByRowKey
    : setLocalViolationsByRowKey;
  const limits = usingPrefetch ? prefetch!.limits : localLimits;
  const setLimits = usingPrefetch ? prefetch!.setLimits : setLocalLimits;
  const ndtBatchLoading = usingPrefetch ? prefetch!.ndtBatchLoading : localNdtBatchLoading;
  const setNdtBatchLoading = usingPrefetch
    ? (_v: boolean | ((prev: boolean) => boolean)) => {
        /* shared prefetch owns loading flags */
      }
    : setLocalNdtBatchLoading;
  const ndtBatchError = usingPrefetch ? prefetch!.ndtBatchError : localNdtBatchError;
  const setNdtBatchError = usingPrefetch
    ? (_v: string | null | ((prev: string | null) => string | null)) => {
        /* shared prefetch owns errors */
      }
    : setLocalNdtBatchError;
  const wpsBatchLoading = usingPrefetch ? prefetch!.wpsBatchLoading : localWpsBatchLoading;
  const setWpsBatchLoading = usingPrefetch
    ? (_v: boolean | ((prev: boolean) => boolean)) => {
        /* shared prefetch owns loading flags */
      }
    : setLocalWpsBatchLoading;

  ndtCacheRef.current = ndtCache;
  violationsByRowKeyRef.current = violationsByRowKey;
  welderCacheRef.current = welderCache;
  passesByRowKeyRef.current = passesByRowKey;
  limitsRef.current = limits;

  const dataSessionKey = useMemo(() => {
    if (prefetch?.sessionKey) return prefetch.sessionKey;
    const pipes =
      appliedPipeIds.length > 0 ? appliedPipeIds : pipeId != null ? [pipeId] : [];
    return (
      buildDrillDownSessionKey(projectId, pipes, appliedJobRefs) ??
      (projectId != null ? `${projectId}|${pipeId ?? ''}` : null)
    );
  }, [prefetch?.sessionKey, projectId, pipeId, appliedPipeIds, appliedJobRefs]);

  const {
    ndtIndicationLogs,
    ndtIndicationLogsLoading,
    ndtIndicationLogsError,
  } = useSelector(
    (state: {
      productionData?: {
        ndtIndicationLogs?: NdtIndicationRow[];
        ndtIndicationLogsLoading?: boolean;
        ndtIndicationLogsError?: string | null;
      };
    }) => state.productionData ?? {},
  );

  const allRows = useMemo(
    () => buildWeldDrillDownRows(metrics, productionWeldData, bundle, projectId),
    [metrics, productionWeldData, bundle, projectId],
  );

  const meta = variant ? VARIANT_META[variant] : null;
  const identityForFilter = weldIdentityFilter ?? (singleWeldOnly ? preselectWeld : null);
  const isScopedDrillDown = Boolean(
    (singleWeldOnly && identityForFilter) || jobOnlyFilter,
  );
  const displayRows = useMemo(() => {
    let rows = meta ? filterByOutcome(allRows, meta.filter) : [];
    if (singleWeldOnly && identityForFilter) {
      rows = rows.filter(
        (r) =>
          String(r.weldNumber) === String(identityForFilter.weldNumber) &&
          String(r.jobNumber) === String(identityForFilter.jobNumber),
      );
    } else if (jobOnlyFilter) {
      rows = rows.filter((r) => String(r.jobNumber) === String(jobOnlyFilter));
    }
    return rows;
  }, [allRows, meta, singleWeldOnly, identityForFilter, jobOnlyFilter]);

  const dialogTitle = useMemo(() => {
    if (!meta) return '';
    if (singleWeldOnly && identityForFilter) {
      const weldLabel = `Weld #${identityForFilter.weldNumber}`;
      return identityForFilter.jobNumber
        ? `${weldLabel} (Job #${identityForFilter.jobNumber})`
        : weldLabel;
    }
    if (jobOnlyFilter) {
      return `Job #${jobOnlyFilter}`;
    }
    return meta.title;
  }, [meta, singleWeldOnly, identityForFilter, jobOnlyFilter]);

  const displayRowKeysSig = useMemo(
    () => displayRows.map((r) => rowKey(r)).join('\0'),
    [displayRows],
  );
  const displayRowsRef = useRef(displayRows);
  displayRowsRef.current = displayRows;

  const resetDataCaches = useCallback(() => {
    if (usingPrefetch) {
      // Prefetch owns session caches; do not wipe shared state from the host.
      return;
    }
    if (dataSessionKeyRef.current) {
      clearWeldDetailCacheForSession(dataSessionKeyRef.current);
    }
    setLocalNdtCache({});
    setLocalWelderCache({});
    setLocalPassesByRowKey({});
    setLocalViolationsByRowKey({});
    limitsRef.current = [];
    setLocalLimits([]);
    limitsLoadedForSessionRef.current = false;
    limitsPromiseRef.current = null;
    ndtInFlightRef.current.clear();
    ndtScopeLogsRef.current = { pipes: new Set(), logs: [] };
  }, [usingPrefetch]);

  const hydrateRowFromCache = useCallback(
    (row: WeldDrillDownRow): boolean => {
      if (!dataSessionKey) return false;
      const key = rowKey(row);
      const cached = getCachedWeldDetail(weldDetailCacheKey(dataSessionKey, key));
      if (!cached) return false;
      setPassesByRowKey((prev) => (key in prev ? prev : { ...prev, [key]: cached.passes }));
      setViolationsByRowKey((prev) =>
        key in prev ? prev : { ...prev, [key]: cached.violations },
      );
      setWelderCache((prev) => (key in prev ? prev : { ...prev, [key]: cached.welderIds }));
      if (cached.ndtLogs) {
        setNdtCache((prev) => (key in prev ? prev : { ...prev, [key]: cached.ndtLogs! }));
      }
      return true;
    },
    [dataSessionKey, setPassesByRowKey, setViolationsByRowKey, setWelderCache, setNdtCache],
  );

  const ensureLimitsLoaded = useCallback(
    async (pid: number): Promise<LimitsRow[]> => {
      if (prefetch) {
        return prefetch.ensureLimitsLoaded(pid);
      }
      if (limitsLoadedForSessionRef.current) {
        return limitsRef.current;
      }
      if (!limitsPromiseRef.current) {
        const sessionAtFetch = dataSessionKeyRef.current;
        limitsPromiseRef.current = fetchProjectLimitsApi(pid)
          .then((data) => {
            if (dataSessionKeyRef.current !== sessionAtFetch) {
              return limitsRef.current;
            }
            limitsRef.current = data.allDataSet;
            setLocalLimits(data.allDataSet);
            limitsLoadedForSessionRef.current = true;
            setLimitsEpoch((n) => n + 1);
            return data.allDataSet;
          })
          .catch((err) => {
            limitsPromiseRef.current = null;
            throw err;
          });
      }
      return limitsPromiseRef.current;
    },
    [prefetch],
  );

  const failedDisplayCount = displayRows.filter((r) => r.outcome === 'fail').length;
  const wpsBatchTargetCount = Math.min(failedDisplayCount, WPS_BATCH_FETCH_CAP);
  const wpsBatchTruncated = usingPrefetch
    ? prefetch!.wpsBatchTruncated
    : failedDisplayCount > WPS_BATCH_FETCH_CAP;
  const ndtLoadedCount = displayRows.filter((r) => rowKey(r) in ndtCache).length;
  const passSummariesLoadedCount = displayRows.filter(
    (r) => r.outcome === 'fail' && rowKey(r) in passesByRowKey,
  ).length;

  /** Hide NDT spinner during Phase B when this dialog only shows already-fetched fail rows. */
  const displayRowsNdtComplete =
    displayRows.length === 0 || displayRows.every((r) => rowKey(r) in ndtCache);
  const ndtProgressVisible = isScopedDrillDown
    ? !displayRowsNdtComplete && ndtBatchLoading
    : ndtBatchLoading &&
      !(
        usingPrefetch &&
        prefetch!.ndtPhase === 'remaining' &&
        variant === 'out' &&
        displayRowsNdtComplete
      );

  const displayRowsWpsComplete =
    failedDisplayCount === 0 || passSummariesLoadedCount >= wpsBatchTargetCount;
  const wpsProgressVisible = isScopedDrillDown
    ? wpsBatchLoading && !displayRowsWpsComplete
    : wpsBatchLoading;

  const liveScoringBatchLoading = usingPrefetch
    ? prefetch!.liveScoringBatchLoading
    : false;
  const liveScoringTargetCount = usingPrefetch ? prefetch!.liveScoringTargetCount : 0;
  const liveScoringLoadedCount = usingPrefetch ? prefetch!.liveScoringLoadedCount : 0;

  const rowsWithNdtBadge = useMemo(
    () =>
      applyNdtCacheToRows(
        displayRows,
        ndtCache,
        selectedRow,
        ndtIndicationLogs ?? null,
      ),
    [displayRows, ndtCache, selectedRow, ndtIndicationLogs],
  );

  const kpis = useMemo(
    () =>
      variant
        ? computeVariantKpis(
            variant,
            displayRows,
            allRows,
            ndtCache,
            ndtBatchLoading,
            violationsByRowKey,
            wpsBatchLoading,
          )
        : [],
    [variant, displayRows, allRows, ndtCache, ndtBatchLoading, violationsByRowKey, wpsBatchLoading],
  );

  const selectedNdtLogs = useMemo(() => {
    if (!selectedRow) return [];
    const cached = ndtCache[rowKey(selectedRow)];
    if (cached) return cached;
    return ndtIndicationLogs ?? [];
  }, [selectedRow, ndtCache, ndtIndicationLogs]);

  const selectedNdtLoading =
    Boolean(selectedRow) &&
    !ndtCache[rowKey(selectedRow!)] &&
    (ndtBatchLoading || Boolean(ndtIndicationLogsLoading));

  const handleClose = useCallback(() => {
    dispatch(clearNDTIndicationLogs());
    setSelectedRow(null);
    setEditNdtOpen(false);
    setWpsPassLoadingKey(null);
    setWpsViolationsLoadingKey(null);
    setWpsDataError(null);
    if (!usingPrefetch) {
      setLocalWpsBatchLoading(false);
      cancelBatch();
      setLocalNdtBatchLoading(false);
      setLocalNdtBatchError(null);
    }
    // Keep NDT / WPS / limits caches so reopening another card reuses them.
    onClose();
  }, [dispatch, onClose, cancelBatch, usingPrefetch]);

  const resolvePipeIdForRow = useCallback(
    (row: WeldDrillDownRow): number | undefined => {
      const raw =
        row.productionRow?.pipeId ??
        row.productionRow?.pipe_details_id ??
        pipeId;
      if (raw == null || raw === '') return undefined;
      const n = Number(raw);
      return Number.isFinite(n) ? n : undefined;
    },
    [pipeId],
  );

  const fetchNdtForRow = useCallback(
    (row: WeldDrillDownRow) => {
      if (!projectId) return;
      const rowPipeId = resolvePipeIdForRow(row);
      dispatch(
        fetchNDTIndicationLogs({
          project_id: projectId,
          weld_id: row.weldNumber,
          ...(rowPipeId != null ? { pipe_id: rowPipeId } : {}),
        }),
      );
    },
    [dispatch, projectId, resolvePipeIdForRow],
  );

  const refreshNdtCacheEntry = useCallback(
    async (row: WeldDrillDownRow) => {
      if (!projectId) return;
      try {
        const rowPipeId = resolvePipeIdForRow(row);
        const logs = await fetchNdtIndicationLogsApi({
          project_id: projectId,
          weld_id: row.weldNumber,
          ...(rowPipeId != null ? { pipe_id: rowPipeId } : {}),
        });
        setNdtCache((prev) => ({ ...prev, [rowKey(row)]: logs }));
      } catch {
        // keep existing cache on refresh failure
      }
    },
    [projectId, resolvePipeIdForRow],
  );

  const loadWpsDataForRow = useCallback(
    async (row: WeldDrillDownRow, options?: { background?: boolean }) => {
      if (!projectId) return;
      const key = rowKey(row);
      const cacheKey = dataSessionKey ? weldDetailCacheKey(dataSessionKey, key) : null;

      const cached = cacheKey ? getCachedWeldDetail(cacheKey) : null;
      if (cached && !options?.background) {
        hydrateRowFromCache(row);
        if (isWeldDetailCacheFresh(cached)) return;
      }

      const hasPasses = key in passesByRowKeyRef.current;
      const hasViolations = key in violationsByRowKeyRef.current;
      const incomplete =
        hasViolations && violationsLackLimits(violationsByRowKeyRef.current[key] ?? []);
      if (hasPasses && hasViolations && !incomplete && cached && isWeldDetailCacheFresh(cached)) {
        return;
      }
      if (wpsInFlightRef.current.has(key)) return;
      wpsInFlightRef.current.add(key);
      try {

      const viewParams = {
        project_id: projectId,
        project_name: row.projectName || undefined,
        weld_number: row.weldNumber,
        job_number: row.jobNumber || undefined,
      };

      let passes = passesByRowKeyRef.current[key] ?? [];
      let zones: Awaited<ReturnType<typeof fetchZoneViewRecords>> = [];
      let tilts: Awaited<ReturnType<typeof fetchTiltViewRecords>> = [];

      if (!hasPasses) {
        if (!options?.background) {
          setWpsPassLoadingKey(key);
        }
        setWpsDataError(null);
        try {
          const interactive = { priority: 0 as const };
          passes = await fetchPassViewRecords(viewParams, interactive);
          setPassesByRowKey((prev) => ({ ...prev, [key]: passes }));
          const welderIds = Array.from(
            new Set(passes.map((p) => p.welderId).filter((id) => id && id !== '—')),
          ).join(', ');
          setWelderCache((prev) => ({ ...prev, [key]: welderIds || '—' }));
        } catch {
          setWpsDataError({ key, message: 'Could not load pass summary for this weld.' });
          setWpsPassLoadingKey((prev) => (prev === key ? null : prev));
          return;
        } finally {
          setWpsPassLoadingKey((prev) => (prev === key ? null : prev));
        }
      }

      if (!hasViolations || incomplete) {
        try {
          const interactive = { priority: 0 as const };
          [zones, tilts] = await Promise.all([
            fetchZoneViewRecords(viewParams, interactive),
            fetchTiltViewRecords(viewParams, interactive),
          ]);
        } catch {
          zones = [];
          tilts = [];
        }
      }

      if (hasViolations && !incomplete) {
        if (cacheKey) {
          setCachedWeldDetail(cacheKey, {
            passes,
            violations: violationsByRowKeyRef.current[key] ?? [],
            welderIds: welderCacheRef.current[key] ?? '—',
            loadedAt: Date.now(),
          });
        }
        return;
      }

      if (!options?.background) {
        setWpsViolationsLoadingKey(key);
      }
      try {
        const limitsRows = await ensureLimitsLoaded(projectId);
        const violations = collectViolationsForWeld(passes, zones, tilts, limitsRows);
        setViolationsByRowKey((prev) => ({ ...prev, [key]: violations }));
        if (cacheKey) {
          setCachedWeldDetail(cacheKey, {
            passes,
            violations,
            welderIds: welderCacheRef.current[key] ?? '—',
            loadedAt: Date.now(),
            wpsError: null,
          });
        }
      } catch {
        setWpsDataError({ key, message: 'Could not load WPS violation data for this weld.' });
        if (cacheKey) {
          setCachedWeldDetail(cacheKey, {
            passes,
            violations: [],
            welderIds: welderCacheRef.current[key] ?? '—',
            loadedAt: Date.now(),
            wpsError: 'Could not load WPS violation data for this weld.',
          });
        }
      } finally {
        setWpsViolationsLoadingKey((prev) => (prev === key ? null : prev));
      }
    } finally {
      wpsInFlightRef.current.delete(key);
    }
    },
    [projectId, dataSessionKey, ensureLimitsLoaded, hydrateRowFromCache],
  );

  const handleRowSelect = useCallback(
    (row: WeldDrillDownRow) => {
      setSelectedRow(row);
      const key = rowKey(row);
      hydrateRowFromCache(row);
      if (!(key in ndtCacheRef.current) && !ndtInFlightRef.current.has(key)) {
        dispatch(clearNDTIndicationLogs());
        fetchNdtForRow(row);
      }
      const needsWps =
        variant === 'out' && row.outcome === 'fail'
          ? true
          : !(key in welderCacheRef.current) || !(key in passesByRowKeyRef.current);
      if (needsWps) {
        const cacheKey = dataSessionKey ? weldDetailCacheKey(dataSessionKey, key) : null;
        const cached = cacheKey ? getCachedWeldDetail(cacheKey) : null;
        loadWpsDataForRow(row, {
          background: cached != null && isWeldDetailCacheFresh(cached),
        });
      }
    },
    [dispatch, fetchNdtForRow, variant, loadWpsDataForRow, hydrateRowFromCache, dataSessionKey],
  );

  const singleRowKey = displayRows.length === 1 ? rowKey(displayRows[0]) : null;

  useEffect(() => {
    if (!variant || preselectWeld || displayRows.length <= 1 || selectedRow) return;
    handleRowSelect(displayRows[0]);
  }, [variant, preselectWeld, displayRowKeysSig, displayRows, selectedRow, handleRowSelect]);

  useEffect(() => {
    if (!variant || !singleRowKey || displayRows.length !== 1) return;
    handleRowSelect(displayRows[0]);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- auto-open once per single-weld set
  }, [variant, singleRowKey]);

  useEffect(() => {
    if (!variant || !preselectWeld) return;
    const match = displayRows.find(
      (r) =>
        String(r.weldNumber) === String(preselectWeld.weldNumber) &&
        String(r.jobNumber) === String(preselectWeld.jobNumber),
    );
    if (match) {
      handleRowSelect(match);
      onPreselectResult?.('selected');
      return;
    }
    if (displayRows.length > 0 || allRows.length > 0) {
      onPreselectResult?.('missing');
    }
  }, [variant, preselectWeld, displayRows, allRows.length, handleRowSelect, onPreselectResult]);

  useEffect(() => {
    if (usingPrefetch) {
      // Apply-time prefetch owns NDT batching; host only hydrates + row-select.
      return;
    }
    if (!variant || !projectId || dataSessionKey == null) {
      setSelectedRow(null);
      setLocalNdtBatchLoading(false);
      setLocalNdtBatchError(null);
      setLocalWpsBatchLoading(false);
      cancelBatch();
      dispatch(clearNDTIndicationLogs());
      return;
    }

    if (dataSessionKeyRef.current !== dataSessionKey) {
      dataSessionKeyRef.current = dataSessionKey;
      resetDataCaches();
    }

    const runId = batchRunRef.current + 1;
    batchRunRef.current = runId;

    const rowsToFetch = displayRowsRef.current.filter(
      (row) => !(rowKey(row) in ndtCacheRef.current),
    );
    if (rowsToFetch.length === 0) {
      setLocalNdtBatchLoading(false);
      setLocalNdtBatchError(null);
      return;
    }

    setLocalNdtBatchLoading(true);
    setLocalNdtBatchError(null);

    void ensureLimitsLoaded(projectId).catch(() => {
      // Limits retry happens when WPS loaders call ensureLimitsLoaded again.
    });

    let ndtCompleted = 0;
    let pendingNdt: Record<string, NdtIndicationRow[]> = {};

    const flushNdtProgress = () => {
      if (Object.keys(pendingNdt).length === 0) return;
      const chunk = pendingNdt;
      pendingNdt = {};
      setLocalNdtCache((prev) => ({ ...prev, ...chunk }));
    };

    rowsToFetch.forEach((row) => ndtInFlightRef.current.add(rowKey(row)));

    (async () => {
      try {
        const pipeIds = Array.from(
          new Set(
            rowsToFetch
              .map((row) => resolvePipeIdForRow(row))
              .filter((id): id is number => id != null),
          ),
        );
        const missingPipes = pipeIds.filter((id) => !ndtScopeLogsRef.current.pipes.has(id));
        if (missingPipes.length > 0 || (pipeIds.length === 0 && ndtScopeLogsRef.current.logs.length === 0)) {
          const fresh = await fetchNdtLogsForPipes(
            projectId,
            missingPipes.length > 0 ? missingPipes : [],
          );
          missingPipes.forEach((id) => ndtScopeLogsRef.current.pipes.add(id));
          ndtScopeLogsRef.current.logs = [...ndtScopeLogsRef.current.logs, ...fresh];
        }
        const scopeLogs = ndtScopeLogsRef.current.logs;
        if (batchRunRef.current !== runId) return;
        if (scopeLogs.length === 0 || logsHaveWeldIdentity(scopeLogs)) {
          const indexed = indexNdtLogsForRows(
            rowsToFetch.map((row) => ({
              key: rowKey(row),
              weldNumber: String(row.weldNumber ?? ''),
              jobNumber: String(row.jobNumber ?? ''),
            })),
            scopeLogs,
          );
          pendingNdt = indexed;
          flushNdtProgress();
        } else {
          await runWithConcurrency(rowsToFetch, NDT_BATCH_CONCURRENCY, async (row) => {
            const key = rowKey(row);
            if (batchRunRef.current !== runId) return;
            try {
              const rowPipeId = resolvePipeIdForRow(row);
              const logs = await fetchNdtIndicationLogsApi({
                project_id: projectId,
                weld_id: row.weldNumber,
                ...(rowPipeId != null ? { pipe_id: rowPipeId } : {}),
              });
              if (batchRunRef.current !== runId) return;
              pendingNdt[key] = logs;
            } catch {
              if (batchRunRef.current !== runId) return;
              pendingNdt[key] = [];
            } finally {
              ndtInFlightRef.current.delete(key);
            }
            ndtCompleted += 1;
            if (ndtCompleted <= 5 || ndtCompleted % 5 === 0 || ndtCompleted === rowsToFetch.length) {
              flushNdtProgress();
            }
          });
          if (batchRunRef.current === runId) {
            flushNdtProgress();
          }
        }
      } catch {
        if (batchRunRef.current === runId) {
          setLocalNdtBatchError('Could not load NDT summaries for all welds.');
        }
      } finally {
        rowsToFetch.forEach((row) => ndtInFlightRef.current.delete(rowKey(row)));
        if (batchRunRef.current === runId) {
          setLocalNdtBatchLoading(false);
        }
      }
    })();
  }, [
    usingPrefetch,
    variant,
    projectId,
    pipeId,
    dataSessionKey,
    displayRowKeysSig,
    dispatch,
    cancelBatch,
    resetDataCaches,
    ensureLimitsLoaded,
    resolvePipeIdForRow,
  ]);

  useEffect(() => {
    if (usingPrefetch) {
      return;
    }
    if (variant !== 'out' || !projectId) {
      return;
    }

    const failedRows = displayRowsRef.current.filter((r) => r.outcome === 'fail');
    if (failedRows.length === 0) {
      setLocalWpsBatchLoading(false);
      return;
    }

    const existingKeys = new Set(
      Object.entries(violationsByRowKeyRef.current)
        .filter(([, vs]) => Array.isArray(vs) && !violationsLackLimits(vs))
        .map(([key]) => key),
    );
    const missingRows = failedRows.filter((r) => !existingKeys.has(rowKey(r)));
    if (missingRows.length === 0) {
      setLocalWpsBatchLoading(false);
      return;
    }

    let cancelled = false;
    setLocalWpsBatchLoading(true);

    const mergePartial = (partial: {
      violations: Record<string, WpsViolationRecord[]>;
      welders: Record<string, string>;
      passes: Record<string, PassViewRecord[]>;
    }) => {
      if (cancelled) return;
      setLocalViolationsByRowKey((prev) => ({ ...prev, ...partial.violations }));
      setLocalWelderCache((prev) => ({ ...prev, ...partial.welders }));
      setLocalPassesByRowKey((prev) => ({ ...prev, ...partial.passes }));
      if (dataSessionKey) {
        Object.keys(partial.passes).forEach((k) => {
          setCachedWeldDetail(weldDetailCacheKey(dataSessionKey, k), {
            passes: partial.passes[k] ?? [],
            violations: partial.violations[k] ?? [],
            welderIds: partial.welders[k] ?? '—',
            loadedAt: Date.now(),
          });
        });
      }
    };

    (async () => {
      let limitsRows: LimitsRow[] | null = null;
      try {
        limitsRows = await ensureLimitsLoaded(projectId);
      } catch {
        limitsRows = null;
      }
      if (cancelled) return;

      const result = await loadBatch(
        projectId,
        missingRows,
        limitsRows,
        existingKeys,
        mergePartial,
      );
      if (cancelled) return;
      setLocalViolationsByRowKey((prev) => ({ ...prev, ...result.violations }));
      setLocalWelderCache((prev) => ({ ...prev, ...result.welders }));
      setLocalPassesByRowKey((prev) => ({ ...prev, ...result.passes }));
      if (result.limits) {
        limitsRef.current = result.limits;
        limitsLoadedForSessionRef.current = true;
        setLocalLimits(result.limits);
      }
      setLocalWpsBatchLoading(false);
    })();

    return () => {
      cancelled = true;
      cancelBatch();
    };
  }, [
    usingPrefetch,
    variant,
    projectId,
    displayRowKeysSig,
    limitsEpoch,
    loadBatch,
    cancelBatch,
    ensureLimitsLoaded,
    dataSessionKey,
  ]);

  const handleEditNdt = () => {
    if (!selectedRow) return;
    setEditNdtOpen(true);
  };

  const handleNdtModalClose = () => {
    setEditNdtOpen(false);
    if (selectedRow) {
      refreshNdtCacheEntry(selectedRow);
      if (!ndtCache[rowKey(selectedRow)]) {
        fetchNdtForRow(selectedRow);
      }
    }
  };

  const selectedRowKey = selectedRow ? rowKey(selectedRow) : null;
  const selectedWelderIds = selectedRowKey ? welderCache[selectedRowKey] ?? '—' : '—';
  const selectedPassRecords = selectedRowKey ? passesByRowKey[selectedRowKey] ?? [] : [];
  const selectedPassLoading = Boolean(
    selectedRowKey &&
      wpsPassLoadingKey === selectedRowKey &&
      !(selectedRowKey in passesByRowKey),
  );
  const selectedViolationsLoading = Boolean(
    selectedRowKey && wpsViolationsLoadingKey === selectedRowKey,
  );
  const selectedViolations = selectedRowKey ? violationsByRowKey[selectedRowKey] ?? [] : [];
  const showWpsViolationDetail = variant === 'out' && selectedRow?.outcome === 'fail';
  const selectedLimitsConfigured = selectedRow
    ? limitsConfiguredForJob(limits, selectedRow.jobNumber)
    : true;

  const open = variant != null;

  return (
    <>
      <Dialog open={open} onOpenChange={(isOpen) => !isOpen && handleClose()}>
        <DialogContent className="flex h-[100dvh] w-[100vw] max-h-[100dvh] max-w-[100vw] flex-col gap-3 overflow-hidden rounded-none border-0 p-4 sm:p-5">
          {meta && (
            <>
              <DialogHeader className="shrink-0">
                <DialogTitle className="font-heading text-blueprint-blue">
                  {dialogTitle}
                  <span className="ml-2 rounded-full bg-[rgba(0,39,63,0.06)] px-2.5 py-0.5 text-sm font-semibold text-heritage-grey">
                    {displayRows.length}
                  </span>
                </DialogTitle>
              </DialogHeader>

              {(ndtProgressVisible ||
                wpsProgressVisible ||
                (liveScoringBatchLoading && !isScopedDrillDown)) && (
                <div className="flex shrink-0 flex-col gap-1 rounded-lg border border-[#E2E5EA] bg-[rgba(0,39,63,0.03)] px-3 py-2 text-sm text-heritage-grey">
                  {ndtProgressVisible && (
                    <div className="flex items-center gap-2">
                      <Loader2 className="h-4 w-4 shrink-0 animate-spin text-blueprint-blue" />
                      <span>
                        {usingPrefetch && !isScopedDrillDown
                          ? formatNdtPrefetchProgress({
                              phase: prefetch!.ndtPhase,
                              phaseLoadedCount: prefetch!.ndtPhaseLoadedCount,
                              phaseTargetCount: prefetch!.ndtPhaseTargetCount,
                              loadedCount: ndtLoadedCount,
                              totalCount: displayRows.length,
                            })
                          : displayRows.length > 0
                            ? `NDT loaded for ${ndtLoadedCount} of ${displayRows.length} welds…`
                            : 'Loading NDT summaries…'}
                      </span>
                    </div>
                  )}
                  {wpsProgressVisible && (
                    <div className="flex items-center gap-2">
                      {!ndtProgressVisible && (
                        <Loader2 className="h-4 w-4 shrink-0 animate-spin text-blueprint-blue" />
                      )}
                      <span className={ndtProgressVisible ? 'pl-6' : undefined}>
                        {wpsBatchTargetCount > 0
                          ? `Pass summaries loaded for ${Math.min(
                              passSummariesLoadedCount,
                              wpsBatchTargetCount,
                            )} of ${wpsBatchTargetCount} welds…`
                          : Object.keys(violationsByRowKey).length > 0
                            ? 'Updating summaries…'
                            : 'Loading summaries…'}
                      </span>
                    </div>
                  )}
                  {liveScoringBatchLoading &&
                    liveScoringTargetCount > 0 &&
                    !isScopedDrillDown && (
                    <div className="flex items-center gap-2">
                      {!ndtProgressVisible && !wpsProgressVisible && (
                        <Loader2 className="h-4 w-4 shrink-0 animate-spin text-blueprint-blue" />
                      )}
                      <span
                        className={
                          ndtProgressVisible || wpsProgressVisible ? 'pl-6' : undefined
                        }
                      >
                        Live scoring {liveScoringLoadedCount} of{' '}
                        {liveScoringTargetCount} out-of-limit welds…
                      </span>
                    </div>
                  )}
                </div>
              )}

              {ndtBatchError && !ndtBatchLoading && (
                <p className="shrink-0 text-sm text-readable-red">{ndtBatchError}</p>
              )}

              <div className="shrink-0">
                <DrillDownKpiStrip kpis={kpis} />
              </div>

              <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
                  <div className="grid min-h-0 flex-1 gap-4 lg:grid-cols-5">
                    <div className="flex min-h-0 flex-col lg:col-span-2">
                      <WeldDrillDownTable
                        rows={rowsWithNdtBadge}
                        selectedRowKey={selectedRowKey}
                        onRowSelect={handleRowSelect}
                        showOutcomeColumn={meta.showOutcome}
                        showViolationColumns={variant === 'out'}
                        violationsByRowKey={violationsByRowKey}
                      />
                    </div>
                    <div className="flex min-h-0 flex-col lg:col-span-3">
                      {wpsDataError && selectedRow && wpsDataError.key === selectedRowKey && (
                        <p className="mb-2 shrink-0 text-sm text-readable-red">{wpsDataError.message}</p>
                      )}
                      <WeldDrillDownRightPanel
                        selectedRow={selectedRow}
                        statusCalculationMethod={statusCalculationMethod}
                        ndtLogs={selectedNdtLogs}
                        ndtLoading={selectedNdtLoading}
                        ndtError={ndtIndicationLogsError ?? null}
                        canEditNdt={canEditNdt}
                        onEditNdt={handleEditNdt}
                        welderIds={selectedWelderIds}
                        passesLoading={selectedPassLoading}
                        passRecords={selectedPassRecords}
                        wpsViolations={selectedViolations}
                        wpsViolationsLoading={selectedViolationsLoading}
                        showWpsViolationDetail={showWpsViolationDetail}
                        limitsConfigured={selectedLimitsConfigured}
                        dashboardProjectId={projectId}
                        pipeId={
                          selectedRow?.productionRow?.pipeId != null
                            ? String(selectedRow.productionRow.pipeId)
                            : selectedRow?.productionRow?.pipe_details_id != null
                              ? String(selectedRow.productionRow.pipe_details_id)
                              : pipeId
                        }
                        pipeOptions={pipeOptions}
                      />
                    </div>
                  </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      {selectedRow && editNdtOpen && (
        <NdtRepairLogModal
          isOpen={editNdtOpen}
          onClose={handleNdtModalClose}
          weld={mapDrillDownRowToWeldData(
            selectedRow,
            selectedRow.productionRow?.pipeId != null
              ? String(selectedRow.productionRow.pipeId)
              : selectedRow.productionRow?.pipe_details_id != null
                ? String(selectedRow.productionRow.pipe_details_id)
                : pipeId,
            pipeOptions,
          )}
          canEdit={canEditNdt}
        />
      )}
    </>
  );
};
